import React, { useState, useEffect } from "react";
import { ChevronDown, Check } from "lucide-react";
import { Button } from "react-bootstrap";
import CustomeInfoTooltip from "../../common/CustomeInfoTooltip";
// import { getCustomeRoleplayOptions, updateCustomizedRolePlay } from "../../../requests";
import styles from "../../../_css/AddRoleplay.module.css"

// Default data
const defaultEmotions = [
    { name: "neutral", description: "Partner maintains a balanced, professional demeanor" },
    { name: "angry", description: "Partner is hostile and confrontational from the start" },
    { name: "sad", description: "Partner shows irritation and impatience during conversation" },
    { name: "nervous", description: "Partner displays nervousness or worry" },
    { name: "skeptical", description: "Partner shows distrust and questions everything" },
    { name: "aggressive", description: "Partner is confrontational and forceful in communication" },
    { name: "enthusiastic", description: "Partner shows enthusiasm and high energy" },
    { name: "discouraged", description: "Partner appears demotivated and pessimistic" },
    { name: "indifferent", description: "Partner shows little interest or emotional investment" }
];

const defaultLevelAndTheirToughness = {
    low: "The partner is easy to influence or agree with, even if their style is energetic or emotional. Few obstacles to achieving your goal.",
    medium: "The partner asks questions or raises concerns that require some explanation, but will move forward once reassured.",
    high: "The partner actively resists, blocks, or challenges your points until you address their concerns convincingly.",
    extremely_challenging: "The partner maintains maximum resistance while staying realistic. Only very strong, well-structured responses will succeed."
};

const defaultCommunicationStyles = [
    "direct_confrontational",
    "hesitant_questioning",
    "emotional_unpredictable",
    "formal_distant",
    "businesslike_concise",
    "stubborn_persistent",
    "vague_passive",
    "dominant_know_it_all"
];

const defaultDifficultyLevels = [
    "low",
    "medium",
    "high",
    "🔥 Extremely Challenging"
];

const AvatarDifficulty = ({
    handleNext,
    step = 3,
    handleBack,
    previousSelections = {},
    updateSelection,
    rolePlayId,
    isLoading // Destructure isLoading prop
}) => {
    const [selectedEmotion, setSelectedEmotion] = useState("");
    const [selectedStyle, setSelectedStyle] = useState([]);
    const [selectedDifficulty, setSelectedDifficulty] = useState("");
    const [showEmotionDropdown, setShowEmotionDropdown] = useState(false);
    const [hoveredEmotion, setHoveredEmotion] = useState(null);

    // Mock API Data States
    const [emotions, setEmotions] = useState(defaultEmotions);
    const [communicationStyles, setCommunicationStyles] = useState(defaultCommunicationStyles);
    const [difficultyLevels, setDifficultyLevels] = useState(defaultDifficultyLevels);
    const [levelAndTheirToughness, setLevelAndTheirToughness] = useState(defaultLevelAndTheirToughness);

    // Chunking for layout
    const chunkCommunicationStyles = () => [
        communicationStyles.slice(0, 3),
        communicationStyles.slice(3, 6),
        communicationStyles.slice(6, 8)
    ];
    const chunkDifficultyLevels = () => [difficultyLevels];

    // Helper to format strings
    const formatValue = (value) => {
        if (!value) return "";
        return value
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    // Load saved values
    useEffect(() => {
        const stepKey = `step${step}`;
        if (previousSelections && previousSelections[stepKey]) {
            try {
                const prevData = typeof previousSelections[stepKey] === 'string'
                    ? JSON.parse(previousSelections[stepKey])
                    : previousSelections[stepKey];

                if (prevData.avatar_emotion) setSelectedEmotion(prevData.avatar_emotion);
                if (prevData.communication_styles) setSelectedStyle(prevData.communication_styles);
                if (prevData.difficulty_level) setSelectedDifficulty(prevData.difficulty_level);
            } catch (error) {
                console.error("Error parsing previous selections:", error);
            }
        }
    }, [previousSelections, step]);

    // Mock Fetch Options
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                // --- API COMMENTED OUT ---
                // const res = await getCustomeRoleplayOptions();
                console.log("Using default mock options for AvatarDifficulty.");
            } catch (err) {
                console.error("Error fetching options:", err);
            }
        };
        fetchOptions();
    }, []);

    const handleStyleClick = (style) => {
        if (selectedStyle.includes(style)) {
            setSelectedStyle(selectedStyle.filter(item => item !== style));
        } else if (selectedStyle.length < 2) {
            setSelectedStyle([...selectedStyle, style]);
        }
    };

    const handleDifficultyClick = (level) => {
        setSelectedDifficulty(level);
    };

    const handleContinue = async () => {
        // Construct payload
        const selection = {
            // 1. Create the key the API is looking for
            avatar_behaviour: {
                emotion: selectedEmotion,
                styles: selectedStyle
            },
            // 2. Keep the top-level keys if your UI also needs them for "previousSelections" logic
            avatar_emotion: selectedEmotion,
            communication_styles: selectedStyle,
            // 3. Difficulty level
            difficulty_level: selectedDifficulty
        };

        // Proceed directly (Parent handles API)
        updateSelection(step, selection);
        handleNext(selection);
    };

    const onBack = () => {
        handleBack();
    };

    return (
        <div className={styles.stepCard}>
            {/* Header */}
            <div className={styles.headerContainer}>
                <div>
                    <h4 className={styles.title}>
                        Avatar Behavior & Difficulty Level
                    </h4>
                    <p className={styles.subHeader}>
                        The user defines how the AI-avatar behaves during the conversation. This affects realism, resistance, and learning depth of the role play.
                    </p>
                </div>
            </div>

            {/* Emotion Section */}
            <div className={styles.formGroup}>
                <div className={styles.sectionHeaderContainer}>
                    <div className={styles.headerWithTooltip}>
                        <span className={styles.label}>Emotion of the avatar</span>
                    </div>
                    <p className={styles.subHeader} style={{ fontSize: '12px' }}>
                        How does your conversation partner feel at the start of the conversation?
                    </p>
                </div>

                <div className={styles.dropdownContainer}>
                    <button
                        className={`${styles.dropdownButton} ${!selectedEmotion ? styles.placeholder : ''}`}
                        onClick={() => setShowEmotionDropdown(!showEmotionDropdown)}
                    >
                        {selectedEmotion ? formatValue(selectedEmotion) : "Select Emotion"}
                    </button>
                    <ChevronDown
                        size={20}
                        color="#9ca3af"
                        style={{
                            transform: showEmotionDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s',
                            cursor: 'pointer'
                        }}
                        onClick={() => setShowEmotionDropdown(!showEmotionDropdown)}
                    />

                    {showEmotionDropdown && (
                        <div className={styles.dropdownMenu}>
                            {emotions.map((emotion) => (
                                <div
                                    key={emotion.name}
                                    className={`${styles.dropdownItem} ${selectedEmotion === emotion.name ? styles.selected : ''} ${hoveredEmotion === emotion.name ? styles.hovered : ''}`}
                                    onClick={() => {
                                        setSelectedEmotion(emotion.name);
                                        setShowEmotionDropdown(false);
                                    }}
                                    onMouseEnter={() => setHoveredEmotion(emotion.name)}
                                    onMouseLeave={() => setHoveredEmotion(null)}
                                >
                                    <div style={{ display: "flex", width: "100%", gap: "20px" }}>
                                        <span>{formatValue(emotion.name)}</span>
                                        <CustomeInfoTooltip description={emotion.description} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Communication Style Section */}
            <div className={styles.formGroup}>
                <div className={styles.sectionHeaderContainer}>
                    <div className={styles.sectionTitleRow}>
                        <div className={styles.headerWithTooltip}>
                            <span className={styles.label}>Communication style of the avatar</span>
                            <div className={styles.tooltipWrapper}>
                                <CustomeInfoTooltip description={"For a critical partner choose -> 'Direct' and 'Stubborn'."} />
                            </div>
                        </div>
                        <span className={styles.maxSelection}>Max: 2</span>
                    </div>
                    <p className={styles.subHeader} style={{ fontSize: '12px' }}>
                        How does your conversation partner behave during the conversation?
                    </p>
                </div>

                <div className={styles.chipContainer}>
                    {chunkCommunicationStyles().map((row, rowIndex) => (
                        <div key={rowIndex} className={styles.chipRow}>
                            {row.map((style) => (
                                <button
                                    key={style}
                                    onClick={() => handleStyleClick(style)}
                                    className={`${styles.chip} ${selectedStyle.includes(style) ? styles.selected : ''}`}
                                >
                                    {selectedStyle.includes(style) && <Check size={16} />}
                                    {formatValue(style)}
                                </button>
                            ))}
                        </div>
                    ))}
                </div>
            </div>

            {/* Difficulty Level Section */}
            <div className={styles.formGroup}>
                <div className={styles.sectionHeaderContainer}>
                    <span className={styles.label}>Difficulty level of the conversation</span>
                    <p className={styles.subHeader} style={{ fontSize: '12px' }}>
                        How challenging should the conversation be in your scenario?
                    </p>
                </div>

                <div className={styles.chipContainer}>
                    {chunkDifficultyLevels().map((row, rowIndex) => (
                        <div key={rowIndex} className={styles.chipRow}>
                            {row.map((level) => {
                                const isSelected = selectedDifficulty === level;
                                const button = (
                                    <button
                                        key={level}
                                        onClick={() => handleDifficultyClick(level)}
                                        className={`${styles.chip} ${isSelected ? styles.selected : ''}`}
                                    >
                                        {isSelected && <Check size={16} />}
                                        {level === "🔥 Extremely Challenging" ? "Extremely High" : formatValue(level)}
                                    </button>
                                );

                                return level === "🔥 Extremely Challenging" ? (
                                    <CustomeInfoTooltip key={level} description="Not recommended for beginners." showIcon={false}>
                                        {button}
                                    </CustomeInfoTooltip>
                                ) : (
                                    button
                                );
                            })}
                        </div>
                    ))}
                </div>

                {selectedDifficulty && levelAndTheirToughness[selectedDifficulty === "🔥 Extremely Challenging" ? "extremely_challenging" : selectedDifficulty] && (
                    <p className={styles.difficultyDesc}>
                        {
                            levelAndTheirToughness[selectedDifficulty === "🔥 Extremely Challenging" ? "extremely_challenging" : selectedDifficulty]
                        }
                    </p>
                )}
            </div>

            {/* Buttons */}
            <div className={styles.buttonGroup}>
                <Button
                    variant="light"
                    className={styles.backButton}
                    onClick={onBack}
                    disabled={isLoading} // Disable back while loading
                >
                    Back
                </Button>
                <Button
                    className={styles.nextButton}
                    onClick={handleContinue}
                    disabled={isLoading} // Disable continue while loading
                >
                    {isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Saving...
                        </>
                    ) : (
                        "Continue"
                    )}
                </Button>
            </div>
        </div>
    );
};

export default AvatarDifficulty;